// src/components/network/feed/PostCreator.jsx
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Image, Smile, Tag, X, Send, AlertCircle, Building, Video, FileText,
  Newspaper, CalendarDays, MapPin, Link as LinkIcon
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { db, storage } from '../../../firebase/config';
import { 
  collection, addDoc, serverTimestamp, doc, query, where, limit, getDocs, getDoc 
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

const PostCreator = ({ onPostCreated }) => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [postType, setPostType] = useState('post'); // 'post' | 'article' | 'event'
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentName, setDocumentName] = useState('');
  // Article fields
  const [articleTitle, setArticleTitle] = useState('');
  const [articleSubtitle, setArticleSubtitle] = useState('');
  // Event fields
  const [eventVenue, setEventVenue] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventBookingLink, setEventBookingLink] = useState('');

  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  // Fetch business profile for the current user
  React.useEffect(() => {
    const fetchBusinessProfile = async () => {
      if (!currentUser) return;
      
      try {
        // First try to find a business where the user is the owner
        const ownedBusinessesQuery = query(
          collection(db, 'businesses'),
          where('createdBy', '==', currentUser.uid),
          limit(1)
        );
        
        const ownedBusinessesSnapshot = await getDocs(ownedBusinessesQuery);
        
        if (!ownedBusinessesSnapshot.empty) {
          setBusinessProfile({
            id: ownedBusinessesSnapshot.docs[0].id,
            ...ownedBusinessesSnapshot.docs[0].data()
          });
          return;
        }
        
        // If no businesses owned, check if user is part of any business
        // This would depend on your schema - adjust as needed
      } catch (err) {
        console.error('Error fetching business profile:', err);
      }
    };
    
    fetchBusinessProfile();
  }, [currentUser]);
  
  // Handle file selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
      return;
    }
    
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }
    
    setImage(file);
    setError('');
    
    // Create a preview
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  // Handle removing the image
  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Extract YouTube video ID from URL
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Handle document file selection
  const handleDocumentSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain'
    ];

    if (!validTypes.includes(file.type)) {
      setError('Supported documents: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Document must be less than 10MB');
      return;
    }

    setDocumentFile(file);
    setDocumentName(file.name);
    setError('');
  };

  // Handle removing the document
  const handleRemoveDocument = () => {
    setDocumentFile(null);
    setDocumentName('');
    if (docInputRef.current) {
      docInputRef.current.value = '';
    }
  };

  // Handle removing video URL
  const handleRemoveVideo = () => {
    setVideoUrl('');
    setShowVideoInput(false);
  };

  // Handle adding a tag
  const handleAddTag = (e) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!tagInput.trim()) return;
    
    // Remove special characters and spaces
    const formattedTag = tagInput.trim().toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '');
    
    if (formattedTag && !tags.includes(formattedTag) && tags.length < 5) {
      setTags([...tags, formattedTag]);
      setTagInput('');
    }
  };
  
  // Handle removing a tag
  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: '/network' } });
      return;
    }
    
    // Validate based on post type
    if (postType === 'article' && !articleTitle.trim()) {
      setError('Please enter a title for your article');
      return;
    }
    if (postType === 'event' && !articleTitle.trim()) {
      setError('Please enter a title for your event');
      return;
    }
    if (postType === 'event' && !eventDate) {
      setError('Please select a date for your event');
      return;
    }
    if (!content.trim()) {
      setError('Please enter some content for your post');
      return;
    }
    
    try {
      setUploading(true);
      setError('');
      
      let imageUrl = null;
      let documentUrl = null;
      let savedDocumentName = null;

      // Upload image if present
      if (image) {
        const storageRef = ref(storage, `posts/${currentUser.uid}/${uuidv4()}_${image.name}`);
        const uploadTask = uploadBytesResumable(storageRef, image);

        // Monitor upload progress
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              console.error('Upload error:', error);
              setError('Failed to upload image. Please try again.');
              reject(error);
            },
            async () => {
              imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      // Upload document if present
      if (documentFile) {
        const docStorageRef = ref(storage, `posts/${currentUser.uid}/docs/${uuidv4()}_${documentFile.name}`);
        const docUploadTask = uploadBytesResumable(docStorageRef, documentFile);

        await new Promise((resolve, reject) => {
          docUploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              console.error('Document upload error:', error);
              setError('Failed to upload document. Please try again.');
              reject(error);
            },
            async () => {
              documentUrl = await getDownloadURL(docUploadTask.snapshot.ref);
              savedDocumentName = documentFile.name;
              resolve();
            }
          );
        });
      }

      // Extract YouTube video ID if URL provided
      const youtubeId = extractYouTubeId(videoUrl.trim());

      // Create post document
      const postData = {
        postType,
        content,
        authorId: currentUser.uid,
        businessId: businessProfile?.id || null,
        authorName: businessProfile?.name || userProfile?.firstName
                ? `${userProfile.firstName} ${userProfile.lastName}`
                : currentUser.email,
        authorLogoUrl: businessProfile?.businessLogoUrl || null,
        authorType: businessProfile ? 'business' : 'user',
        imageUrl,
        videoUrl: youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : null,
        documentUrl: documentUrl || null,
        documentName: savedDocumentName || null,
        // Article fields
        title: (postType === 'article' || postType === 'event') ? articleTitle.trim() : null,
        subtitle: postType === 'article' ? articleSubtitle.trim() || null : null,
        // Event fields
        eventVenue: postType === 'event' ? eventVenue.trim() || null : null,
        eventDate: postType === 'event' ? eventDate || null : null,
        eventBookingLink: postType === 'event' ? eventBookingLink.trim() || null : null,
        tags: tags.length > 0 ? tags : [],
        likeCount: 0,
        commentCount: 0,
        viewCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'posts'), postData);
      
      // Format the newly created post for the UI
      const newPost = {
        id: docRef.id,
        ...postData,
        createdAt: new Date(),
        author: {
          id: businessProfile?.id || currentUser.uid,
          name: postData.authorName,
          businessLogoUrl: postData.authorLogoUrl,
          type: postData.authorType
        }
      };
      
      // Notify parent component
      if (onPostCreated) {
        onPostCreated(newPost);
      }
      
      // Reset form
      setPostType('post');
      setContent('');
      setTags([]);
      setImage(null);
      setImagePreview(null);
      setVideoUrl('');
      setShowVideoInput(false);
      setDocumentFile(null);
      setDocumentName('');
      setArticleTitle('');
      setArticleSubtitle('');
      setEventVenue('');
      setEventDate('');
      setEventBookingLink('');
      setUploadProgress(0);
      
    } catch (err) {
      console.error('Error creating post:', err);
      setError('Failed to create post. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  
  if (!currentUser) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <p className="text-gray-600 text-center mb-4">
          Sign in to create posts and interact with the business network
        </p>
        <div className="flex justify-center gap-4">
          <Link 
            to="/login" 
            className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5]"
          >
            Sign In
          </Link>
          <Link 
            to="/register" 
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Register
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 flex">
        <div className="w-10 h-10 rounded-full bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
          {businessProfile?.businessLogoUrl ? (
            <img 
              src={businessProfile.businessLogoUrl} 
              alt={businessProfile.name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
              <Building size={18} />
            </div>
          )}
        </div>
        
        <form className="flex-1" onSubmit={handleSubmit}>
          {/* Post Type Selector */}
          <div className="flex gap-1 mb-3 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setPostType('post')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                postType === 'post'
                  ? 'bg-white text-[#190192] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              disabled={uploading}
            >
              <Send size={14} />
              Post
            </button>
            <button
              type="button"
              onClick={() => setPostType('article')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                postType === 'article'
                  ? 'bg-white text-[#190192] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              disabled={uploading}
            >
              <Newspaper size={14} />
              Article
            </button>
            <button
              type="button"
              onClick={() => setPostType('event')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                postType === 'event'
                  ? 'bg-white text-[#190192] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              disabled={uploading}
            >
              <CalendarDays size={14} />
              Event
            </button>
          </div>

          {/* Article fields */}
          {postType === 'article' && (
            <div className="mb-3 space-y-2">
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-lg font-semibold focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="Article title"
                value={articleTitle}
                onChange={(e) => setArticleTitle(e.target.value)}
                disabled={uploading}
                maxLength={120}
              />
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="Subtitle (optional)"
                value={articleSubtitle}
                onChange={(e) => setArticleSubtitle(e.target.value)}
                disabled={uploading}
                maxLength={200}
              />
            </div>
          )}

          {/* Event fields */}
          {postType === 'event' && (
            <div className="mb-3 space-y-2">
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-lg font-semibold focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="Event title"
                value={articleTitle}
                onChange={(e) => setArticleTitle(e.target.value)}
                disabled={uploading}
                maxLength={120}
              />
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    placeholder="Venue / Location"
                    value={eventVenue}
                    onChange={(e) => setEventVenue(e.target.value)}
                    disabled={uploading}
                  />
                </div>
                <div className="relative">
                  <CalendarDays size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="datetime-local"
                    className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    disabled={uploading}
                  />
                </div>
              </div>
              <div className="relative">
                <LinkIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="url"
                  className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                  placeholder="Booking / RSVP link (optional)"
                  value={eventBookingLink}
                  onChange={(e) => setEventBookingLink(e.target.value)}
                  disabled={uploading}
                />
              </div>
            </div>
          )}

          <div className="mb-3">
            <textarea
              className="w-full border border-gray-200 rounded-lg p-3 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20 resize-none"
              placeholder={
                postType === 'article' ? 'Write your article...' :
                postType === 'event' ? 'Describe your event...' :
                `What would you like to share, ${userProfile?.firstName || 'there'}?`
              }
              rows={postType === 'article' ? 6 : 3}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={uploading}
            ></textarea>
          </div>
          
          {/* Image preview */}
          {imagePreview && (
            <div className="mb-3 relative">
              <div className="rounded-lg overflow-hidden bg-gray-100 relative max-h-60">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="max-w-full object-contain mx-auto"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 bg-gray-900 bg-opacity-70 text-white rounded-full p-1"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}
          
          {/* YouTube URL input */}
          {showVideoInput && (
            <div className="mb-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type="url"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    placeholder="Paste YouTube link (e.g. https://youtube.com/watch?v=...)"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    disabled={uploading}
                  />
                  {videoUrl && (
                    <button
                      type="button"
                      onClick={handleRemoveVideo}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              {videoUrl && extractYouTubeId(videoUrl) && (
                <div className="mt-2 rounded-lg overflow-hidden bg-black aspect-video max-h-48">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(videoUrl)}?mute=1`}
                    title="YouTube preview"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              {videoUrl && !extractYouTubeId(videoUrl) && (
                <p className="text-xs text-red-500 mt-1">Please enter a valid YouTube URL</p>
              )}
            </div>
          )}

          {/* Document preview */}
          {documentName && (
            <div className="mb-3 flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <FileText size={20} className="text-[#190192] flex-shrink-0" />
              <span className="text-sm text-gray-700 truncate flex-1">{documentName}</span>
              <button
                type="button"
                onClick={handleRemoveDocument}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* Tags input and display */}
          {tags.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {tags.map(tag => (
                <div 
                  key={tag} 
                  className="bg-purple-50 text-[#190192] px-2 py-1 rounded-full text-xs flex items-center"
                >
                  #{tag}
                  <button 
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 text-[#190192] hover:text-[#2C0DB5]"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-center space-x-2">
              {/* Image upload button */}
              <button 
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-gray-500 hover:text-[#190192] hover:bg-gray-100 rounded-full"
                disabled={uploading}
              >
                <Image size={18} />
              </button>
              <input 
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageSelect}
              />
              
              {/* YouTube video button */}
              <button
                type="button"
                onClick={() => setShowVideoInput(!showVideoInput)}
                className={`p-2 rounded-full ${showVideoInput || videoUrl ? 'text-[#190192] bg-purple-50' : 'text-gray-500 hover:text-[#190192] hover:bg-gray-100'}`}
                disabled={uploading}
                title="Add YouTube video"
              >
                <Video size={18} />
              </button>

              {/* Document upload button */}
              <button
                type="button"
                onClick={() => docInputRef.current?.click()}
                className={`p-2 rounded-full ${documentFile ? 'text-[#190192] bg-purple-50' : 'text-gray-500 hover:text-[#190192] hover:bg-gray-100'}`}
                disabled={uploading}
                title="Attach document"
              >
                <FileText size={18} />
              </button>
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                className="hidden"
                onChange={handleDocumentSelect}
              />

              {/* Emoji button */}
              <button
                type="button"
                className="p-2 text-gray-500 hover:text-[#190192] hover:bg-gray-100 rounded-full"
                disabled={uploading}
              >
                <Smile size={18} />
              </button>
              
              {/* Tag input */}
              <div className="relative">
                <button 
                  type="button"
                  className="p-2 text-gray-500 hover:text-[#190192] hover:bg-gray-100 rounded-full"
                  onClick={() => document.getElementById('tagInput').focus()}
                  disabled={uploading || tags.length >= 5}
                >
                  <Tag size={18} />
                </button>
                <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-64 z-10 hidden focus-within:block hover:block">
                  {/* Remove the nested form element and use a div instead */}
                  <div className="flex">
                    <input
                      id="tagInput"
                      type="text"
                      className="flex-1 border border-gray-200 rounded-l-lg px-2 py-1 text-sm focus:outline-none focus:border-[#190192]"
                      placeholder="Add tag (max 5)"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      disabled={uploading || tags.length >= 5}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                    />
                    <button
                      type="button" // Important: Changed to type="button" to prevent form submission
                      className="px-2 py-1 bg-[#190192] text-white rounded-r-lg text-sm hover:bg-[#2C0DB5]"
                      disabled={uploading || tags.length >= 5 || !tagInput.trim()}
                      onClick={handleAddTag}
                    >
                      Add
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Add relevant tags to help others find your post
                  </p>
                </div>
              </div>
            </div>
            
            {/* Post button */}
            <button
              type="submit"
              className="px-4 py-2 bg-[#190192] text-[#f6f5f5] rounded-lg hover:bg-[#2C0DB5] flex items-center"
              disabled={uploading || !content.trim()}
            >
              {uploading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent mr-2"></div>
                  Posting...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Post
                </>
              )}
            </button>
          </div>
          
          {/* Upload progress */}
          {uploading && uploadProgress > 0 && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-[#190192] h-2 rounded-full" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 text-right mt-1">
                Uploading... {Math.round(uploadProgress)}%
              </p>
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="mt-3 p-2 bg-red-50 rounded-lg flex items-start text-sm text-red-600">
              <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PostCreator;